import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Download, FileText, Heart, Search, ShieldCheck, User, UsersRound } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";
import { CATEGORY_LABELS, STATUS_LABELS, statusPillClass } from "./erLabels";

const CATEGORY_COLORS = ["green", "terra", "green", "terra", "green", "terra", "green", "terra"];

const ROLE_LEVEL = { employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

export default function ErLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notice, setNotice] = useState({ text: "", updated_at: null });
  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState("");
  const [myReports, setMyReports] = useState([]);
  const [poStats, setPoStats] = useState({ cases: 0, sla_overdue: 0 });

  const level = ROLE_LEVEL[user?.role || "employee"] || 1;
  const isPO = level >= 3;

  const load = async () => {
    try {
      const [n, r, m] = await Promise.all([
        api.get("/er/notice"),
        api.get("/er/resources", { params: search ? { q: search } : {} }),
        api.get("/er/my-reports"),
      ]);
      setNotice(n.data);
      setResources(r.data);
      setMyReports(m.data);
      if (isPO) {
        const c = await api.get("/er/cases");
        setPoStats({
          cases: (c.data.cases || []).length,
          sla_overdue: c.data.sla_overdue_count || 0,
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
     
  }, [search]);

  const downloadResource = async (r) => {
    try {
      const resp = await api.get(`/er/resources/${r.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: r.mime_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || `${r.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10" data-testid="er-landing">
      {/* Hero */}
      <section className="patria-card p-7 sm:p-9 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-terracotta-500/10" aria-hidden />
        <div className="absolute -left-24 -bottom-24 h-56 w-56 rounded-full bg-forest-600/10" aria-hidden />
        <div className="relative">
          <ModuleTitleER />
          <h1
            className="mt-4 font-serif text-forest-900 text-4xl sm:text-5xl leading-tight max-w-3xl"
            data-testid="er-landing-headline"
          >
            A Safe, Confidential Place To Speak Up.
          </h1>
          <p className="mt-4 text-charcoal-soft max-w-2xl leading-relaxed">
            Every teammate deserves to be seen and heard. Use ER Center to raise a concern,
            ask a policy question, or request support — identified or anonymously. People
            Operations will honor your dignity and walk with you through what comes next.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              className="patria-btn-primary"
              onClick={() => navigate("/er/intake")}
              data-testid="er-cta-intake"
            >
              Raise a concern <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </button>
            <Link
              to="/er/anonymous"
              className="patria-btn-secondary inline-flex items-center"
              data-testid="er-cta-anonymous"
            >
              Report Anonymously
            </Link>
            <Link
              to="/er/anonymous/portal"
              className="patria-btn-secondary inline-flex items-center"
              data-testid="er-cta-anon-portal"
            >
              Check On An Anonymous Report
            </Link>
          </div>
        </div>
      </section>

      {/* Notice block */}
      <section className="patria-card p-7 border-l-4 border-terracotta-500" data-testid="er-notice-block">
        <div className="flex items-start gap-4">
          <span className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-terracotta-500 text-cream-100 shrink-0">
            <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
          </span>
          <div>
            <h3 className="font-serif text-forest-900 text-2xl">Your Rights, In Plain Words.</h3>
            <p className="mt-3 text-charcoal-soft whitespace-pre-line leading-relaxed">
              {notice.text || "—"}
            </p>
          </div>
        </div>
      </section>

      {/* Category quick-links */}
      <section>
        <h3 className="font-serif text-forest-900 text-2xl mb-4">Common Concerns We Help With</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(CATEGORY_LABELS).map(([key, label], i) => (
            <button
              key={key}
              onClick={() => navigate(`/er/intake?category=${key}`)}
              data-testid={`er-cat-${key}`}
              className="patria-card patria-card-hover p-4 text-left"
            >
              <span
                className={`inline-flex items-center justify-center h-9 w-9 rounded-full ${
                  CATEGORY_COLORS[i % CATEGORY_COLORS.length] === "green"
                    ? "bg-forest-600"
                    : "bg-terracotta-500"
                } text-cream-100`}
                aria-hidden
              >
                <UsersRound className="h-4 w-4" strokeWidth={1.5} />
              </span>
              <p className="mt-3 font-medium text-forest-900 text-sm">{label}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Resources + My reports */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="patria-card p-6 lg:col-span-2" data-testid="er-resources-panel">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-serif text-forest-900 text-2xl">Policy Library</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted" strokeWidth={1.5} />
              <input
                className="patria-input pl-9 py-2 text-sm w-56"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="er-resources-search"
              />
            </div>
          </div>
          <ul className="mt-4 divide-y divide-sage-300/60">
            {resources.length === 0 && (
              <li className="py-6 text-sm text-charcoal-muted italic">
                No documents match your search.
              </li>
            )}
            {resources.map((r) => (
              <li
                key={r.id}
                className="py-4 flex items-start justify-between gap-4"
                data-testid={`er-resource-${r.id}`}
              >
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-forest-600 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium text-forest-900">{r.title}</p>
                    <p className="text-xs uppercase tracking-widest text-charcoal-muted mt-0.5">
                      {r.category}
                    </p>
                    {r.description && (
                      <p className="text-sm text-charcoal-soft mt-2 max-w-2xl">{r.description}</p>
                    )}
                  </div>
                </div>
                <button
                  className="patria-btn-secondary text-sm py-2 px-3 whitespace-nowrap"
                  onClick={() => downloadResource(r)}
                  data-testid={`er-resource-dl-${r.id}`}
                >
                  <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  Download
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="patria-card p-6" data-testid="er-my-reports-panel">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-forest-600 text-cream-100">
              <User className="h-4 w-4" strokeWidth={1.5} />
            </span>
            <h3 className="font-serif text-forest-900 text-xl">My Reports</h3>
          </div>
          {myReports.length === 0 && (
            <p className="mt-4 text-sm text-charcoal-muted italic">
              You haven&apos;t submitted any identified reports.
            </p>
          )}
          <ul className="mt-4 space-y-3">
            {myReports.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/er/my-reports/${r.id}`}
                  className="block patria-card patria-card-hover p-3"
                  data-testid={`er-my-report-${r.id}`}
                >
                  <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                    {r.case_number}
                  </p>
                  <p className="mt-1 text-sm font-medium text-forest-900">
                    {CATEGORY_LABELS[r.category] || r.category}
                  </p>
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 mt-2 text-xs ${statusPillClass(r.status)}`}
                  >
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {isPO && (
            <div className="mt-6 pt-6 border-t border-sage-300/60">
              <div className="flex items-center gap-3">
                <Heart className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                  People Operations tools
                </p>
              </div>
              <div className="mt-3 space-y-2">
                <Link
                  to="/er/cases"
                  className="block patria-card patria-card-hover p-3"
                  data-testid="er-po-cases-link"
                >
                  <p className="text-sm font-medium text-forest-900">Case Queue</p>
                  <p className="text-xs text-charcoal-muted mt-1">
                    {poStats.cases} active
                    {poStats.sla_overdue > 0 && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-terracotta-500 text-cream-100 px-2 py-0.5 text-[10px] font-medium">
                        {poStats.sla_overdue} SLA overdue
                      </span>
                    )}
                  </p>
                </Link>
                <Link
                  to="/er/resources/admin"
                  className="block patria-card patria-card-hover p-3"
                  data-testid="er-po-resources-link"
                >
                  <p className="text-sm font-medium text-forest-900">Manage Resources</p>
                </Link>
                {user?.role === "super_admin" && (
                  <Link
                    to="/er/notice/edit"
                    className="block patria-card patria-card-hover p-3"
                    data-testid="er-super-notice-link"
                  >
                    <p className="text-sm font-medium text-forest-900">Edit Notice Text</p>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
